import React, { useState } from 'react';
import { X, User, Mail, Phone, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RegisterModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        whatsapp: ''
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('register.errors.name_required') || 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = t('register.errors.email_required') || 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t('register.errors.email_invalid') || 'Email is invalid';
        }

        if (!formData.whatsapp.trim()) {
            newErrors.whatsapp = t('register.errors.whatsapp_required') || 'WhatsApp number is required';
        } else if (!/^\+?\d{10,15}$/.test(formData.whatsapp.replace(/\s/g, ''))) {
            newErrors.whatsapp = t('register.errors.whatsapp_invalid') || 'WhatsApp number is invalid';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            // Here you can add API call to save the data
            console.log('Form submitted:', formData);

            // Show success message
            setIsSubmitted(true);

            // Reset form after 2 seconds and close modal
            setTimeout(() => {
                setFormData({ name: '', email: '', whatsapp: '' });
                setIsSubmitted(false);
                onClose();
            }, 2000);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <style>{`
                .modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.75);
                    backdrop-filter: blur(5px);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    animation: fadeIn 0.3s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .register-modal {
                    background: rgba(15, 15, 25, 0.95);
                    border: 1px solid rgba(255, 215, 0, 0.2);
                    border-radius: 20px;
                    max-width: 500px;
                    width: 100%;
                    padding: 40px;
                    position: relative;
                    backdrop-filter: blur(20px);
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
                    animation: slideUp 0.3s ease;
                }

                .modal-close {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #fff;
                    transition: all 0.3s ease;
                }

                .modal-close:hover {
                    background: rgba(255, 215, 0, 0.2);
                    transform: rotate(90deg);
                }

                .modal-header {
                    text-align: center;
                    margin-bottom: 30px;
                }

                .modal-title {
                    font-size: 2rem;
                    font-weight: 800;
                    background: linear-gradient(135deg, #fff, var(--color-gold));
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    margin-bottom: 10px;
                }

                .modal-subtitle {
                    color: #aaa;
                    font-size: 0.95rem;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-label {
                    display: block;
                    color: #fff;
                    font-weight: 600;
                    margin-bottom: 8px;
                    font-size: 0.9rem;
                    letter-spacing: 0.5px;
                }

                .input-wrapper {
                    position: relative;
                }

                .input-icon {
                    position: absolute;
                    left: 15px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: var(--color-gold);
                }

                .form-input {
                    width: 100%;
                    padding: 14px 14px 14px 45px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 10px;
                    color: #fff;
                    font-size: 0.95rem;
                    outline: none;
                    transition: all 0.3s ease;
                }

                .form-input:focus {
                    border-color: var(--color-gold);
                    background: rgba(255, 255, 255, 0.08);
                    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
                }

                .form-input.error {
                    border-color: #ff4444;
                }

                .error-message {
                    color: #ff4444;
                    font-size: 0.8rem;
                    margin-top: 5px;
                    display: block;
                }

                .submit-btn {
                    width: 100%;
                    padding: 16px;
                    background: linear-gradient(135deg, var(--color-gold), #ffed4e);
                    border: none;
                    border-radius: 10px;
                    color: #000;
                    font-weight: 700;
                    font-size: 1rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-top: 10px;
                }

                .submit-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
                }

                .submit-btn:active {
                    transform: translateY(0);
                }

                .success-message {
                    text-align: center;
                    padding: 40px 20px;
                }

                .success-icon {
                    color: #4CAF50;
                    margin-bottom: 20px;
                    animation: scaleIn 0.5s ease;
                }

                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(0);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                .success-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #fff;
                    margin-bottom: 10px;
                }

                .success-text {
                    color: #aaa;
                    font-size: 0.95rem;
                }

                @media (max-width: 600px) {
                    .register-modal {
                        padding: 30px 20px;
                    }

                    .modal-title {
                        font-size: 1.5rem;
                    }
                }
            `}</style>

            <div className="modal-overlay" onClick={onClose}>
                <div className="register-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>

                    {!isSubmitted ? (
                        <>
                            <div className="modal-header">
                                <h2 className="modal-title">{t('register.title') || 'Register Now'}</h2>
                                <p className="modal-subtitle">
                                    {t('register.subtitle') || 'Join AL-ZAEEM AL-AINAWI and discover premium properties'}
                                </p>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label className="form-label">
                                        {t('register.name') || 'Full Name'}
                                    </label>
                                    <div className="input-wrapper">
                                        <User className="input-icon" size={18} />
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className={`form-input ${errors.name ? 'error' : ''}`}
                                            placeholder={t('register.name_placeholder') || 'Enter your full name'}
                                        />
                                    </div>
                                    {errors.name && <span className="error-message">{errors.name}</span>}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        {t('register.email') || 'Email Address'}
                                    </label>
                                    <div className="input-wrapper">
                                        <Mail className="input-icon" size={18} />
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`form-input ${errors.email ? 'error' : ''}`}
                                            placeholder={t('register.email_placeholder') || 'Enter your email'}
                                        />
                                    </div>
                                    {errors.email && <span className="error-message">{errors.email}</span>}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        {t('register.whatsapp') || 'WhatsApp Number'}
                                    </label>
                                    <div className="input-wrapper">
                                        <Phone className="input-icon" size={18} />
                                        <input
                                            type="tel"
                                            name="whatsapp"
                                            value={formData.whatsapp}
                                            onChange={handleChange}
                                            className={`form-input ${errors.whatsapp ? 'error' : ''}`}
                                            placeholder={t('register.whatsapp_placeholder') || '+971 52 113 8999'}
                                        />
                                    </div>
                                    {errors.whatsapp && <span className="error-message">{errors.whatsapp}</span>}
                                </div>

                                <button type="submit" className="submit-btn">
                                    {t('register.submit') || 'Register'}
                                </button>
                            </form>
                        </>
                    ) : (
                        <div className="success-message">
                            <CheckCircle className="success-icon" size={64} />
                            <h3 className="success-title">
                                {t('register.success_title') || 'Registration Successful!'}
                            </h3>
                            <p className="success-text">
                                {t('register.success_message') || 'Thank you for registering. We will contact you soon.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default RegisterModal;
